import { BlogAuthorService } from './../../services/blog-author-service';
import { EventAggregator, inject, observable, watch } from 'aurelia';
import { IRouter, IRouteViewModel, Params, route } from '@aurelia/router-lite';
import { DEVICES, SortArr } from '../../resources/constants';
import { BlogService } from '../../services/blog-service';
import { BlogTagService } from '../../services/blog-tag-service';
import { WebsiteService } from '../../services/website-service';
import { websiteShortCode } from '../../environment';
import { BlogsTags, ItemsSortBy, SubCategory } from '../../services/models/blogs/blogsTags';
import './blogs.scss';
import { CdsAutocomplete } from '@chicksgroup/web-components';

@route({
    path: ['', 'category/:category'],
    title: 'Blogs',
})
@inject(BlogService, EventAggregator, WebsiteService, IRouter, BlogTagService, BlogAuthorService)
export class Blogs implements IRouteViewModel {
    blogs;
    searchQuery;
    filteredBlogs;
    arrowTriggered: HTMLElement;
    allTagsOptions;
    blogRoute;
    category;
    @observable tagSelected: string;
    lastBlogs;
    homePageRoute;
    routerSubscription;
    params;
    blogsCount: number;
    count: number = 1;
    @observable page = 1;
    perPage: number = 15;
    sizeChanged: any;
    width: number = window.innerWidth;
    isAttached = false;
    startingIndex: number;
    optionSelected: string = 'Popular';
    arrowRotated: boolean;
    lastEventTriggered: string;
    items: ItemsSortBy[];
    authors: ItemsSortBy[];
    categories: ItemsSortBy[];
    subCategories: SubCategory[] = [];
    pageLoading: boolean = true;
    postLoading: boolean = false;
    skeletonPosts = Array.from({ length: 15 });
    blogsResults = true;
    sortBy: string = 'Popular';
    DEVICES = DEVICES;
    shortCode: string = websiteShortCode() ?? 'CG';
    firstLoad: boolean = true;
    sortRef: CdsAutocomplete;
    authorRef: CdsAutocomplete;
    categoryRef: CdsAutocomplete;
    subCategoriesRef: CdsAutocomplete[];
    blogposts: any;

    constructor(
        private blogService: BlogService,
        private eventAggregator: EventAggregator,
        private websiteService: WebsiteService,
        private router: IRouter,
        private blogTagService: BlogTagService,
        private blogAuthorService: BlogAuthorService,
    ) {}

    async loading(params: Params) {
        this.pageLoading = true;
        this.params = params.category || params.page ? params : { category: 'all', page: '1' };
        this.category = this.params.category ? this.params.category : 'all';
    }

    async attaching() {
        this.items = SortArr;
        const pages = await this.websiteService.getPagesByWebsiteShortcode();
        this.allTagsOptions = await this.blogTagService.getAll();
        this.params.page ? (this.page = parseInt(this.params.page)) : (this.page = 1);
        this.params.category ? (this.tagSelected = this.findTagOption(this.category)) : (this.tagSelected = this.findTagOption('all'));
        this.blogRoute = pages.find((x) => x.name === 'Blog')?.routeName ?? 'blog';
        this.homePageRoute = pages.find((x) => x.name === 'Home')?.routeName ?? '';
        this.createListAuthors();
        this.createListCategories();
        await this.pageByWidth();
        this.createListSubCategories();
    }

    async attached() {
        this.loadData();
    }

    async loadData() {
        this.handleEventSubscriptions();
        this.pageLoading = false;
    }

    detaching() {
        this.sizeChanged?.dispose();
        this.routerSubscription?.dispose();
    }

    @watch('blogsResults')
    async searchBlog(event: InputEvent) {
        this.postLoading = true;

        if (this.width >= DEVICES.DESKTOP && !this.lastBlogs) {
            this.lastBlogs = await this.blogService.getPublishedBlogPosts(5, 1);
        } else if (this.width < DEVICES.DESKTOP && !this.lastBlogs) {
            this.lastBlogs = await this.blogService.getPublishedBlogPosts(4, 1);
        }

        this.searchQuery = (event.target as HTMLInputElement).value;
        if (!this.searchQuery) {
            await this.pageChanged();
            return (this.postLoading = false);
        }

        await this.pageChanged();

        if (this.blogsCount === 0) {
            this.blogposts = [];
            this.count = this.page = 1;
            this.blogsResults = false;
            return (this.postLoading = false);
        }

        this.blogsResults = true;
        return (this.postLoading = false);
    }

    handleEventSubscriptions() {
        this.routerSubscription = this.eventAggregator.subscribe('router:navigation:processing', (payload) => {
            const regExp = new RegExp(`(\\W|^)${this.blogRoute}(\\W|$)`, 'i');
            if (
                payload.instruction.params?.blog ||
                payload.instruction.params?.alias ||
                !payload.instruction.fragment.match(regExp)
            )
                return;
            this.category = payload.instruction.params?.category ?? 'all';
            this.page = payload.instruction.params?.page ? parseInt(payload.instruction.params?.page) : 1;
        });
    }

    async pageByWidth() {
        this.postLoading = true;
        if (this.category !== 'all') {
            this.tagClickHandler(this.tagSelected);
        }
        if (this.width >= DEVICES.DESKTOP) {
            this.perPage = 15;
        } else if (this.width < DEVICES.PHONE) {
            this.perPage = 6;
            this.skeletonPosts = Array.from({ length: 6 });
        } else {
            this.perPage = 8;
            this.skeletonPosts = Array.from({ length: 8 });
        }

        await this.pageChanged();
        return (this.postLoading = false);
    }

    async changeCheckboxHandler(event: CustomEvent) {
        const eventItemSelected = event?.selectedItem as ItemsSortBy;

        if (eventItemSelected) {
            if (eventItemSelected.label === 'All') {
                this.categories.forEach((category) => {
                    category.selected = eventItemSelected.selected;
                });
            } else {
                const allSelected = this.categories
                    .filter((category) => category.label !== 'All')
                    .every((category) => category.selected);

                const allCategory = this.categories.find((category) => category.label === 'All');
                if (allCategory) {
                    allCategory.selected = allSelected;
                }
            }
        }

        this.createListSubCategories();
        return this.pageChanged();
    }

    changeSelectHandler() {
        return this.pageChanged();
    }

    async pageChanged() {
        if (this.tagSelected) {
            this.categories.forEach((category) => {
                if (category.label === this.tagSelected?.name) {
                    category.selected = true;
                } else {
                    category.selected = false;
                }
            });
        }

        const tagsValues = this.categories.filter((x: ItemsSortBy) => x.selected === true);
        const tagsArr = tagsValues.filter((x: ItemsSortBy) => x.value !== null).map((x: ItemsSortBy) => x.value || undefined);
        const authorAliasValue = this.authors.find((x: ItemsSortBy) => x.selected === true)?.value;
        const noTagsSelected = tagsArr.length === 0 && !this.categories.find((c) => c.label === 'All')?.selected;
        const selectedSubcategoryTags: string[] = [];

        this.subCategories.forEach((subCategory) => {
            const selectedItems = subCategory.items.filter((item) => item.selected && item.value !== 'all');
            const allSelected = subCategory.items.find((item) => item.value === 'all')?.selected;

            if (selectedItems.length === 0 || allSelected) {
                return;
            }

            selectedItems.forEach((item) => {
                if (item.value) {
                    selectedSubcategoryTags.push(item.value);
                }
            });
        });

        const finalTagsArr = selectedSubcategoryTags.length > 0 ? [...tagsArr, ...selectedSubcategoryTags] : tagsArr;

        if (this.firstLoad) {
            this.firstLoad = false;
        } else {
            this.postLoading = true;
            this.scrollToBlogs();
        }

        this.blogs = await this.blogService.filterBlogs({
            tag: this.tagSelected ? this.tagSelected?.name : undefined,
            tags: noTagsSelected ? (!this.tagSelected ? [''] : undefined) : finalTagsArr,
            content: this.searchQuery,
            page: this.page,
            pageSize: this.perPage,
            published: true,
            order: this.items.find((x: ItemsSortBy) => x.selected === true)?.value ?? 'popular',
            websites: [this.shortCode],
            tagHidden: this.tagSelected?.hideFromAll ?? false,
            authorAlias: authorAliasValue === 'all' ? undefined : authorAliasValue,
        });

        this.blogposts = this.blogs.blogPosts;
        this.blogsCount = this.blogs.totalCount;
        this.count = Math.ceil(this.blogsCount / this.perPage);
        if (this.page > this.count && this.count > 0) {
            this.page = this.count;
        }

        this.scrollToTop();
        return (this.postLoading = false);
    }

    @watch('category')
    handleCategoryChanged() {
        const newTag = this.findTagOption(this.category);
        this.tagClickHandler(newTag);
    }

    async tagClickHandler(tag: BlogsTags) {
        if (!tag) return;
        this.tagSelected = tag;
        return await this.urlConstructor(this.tagSelected.slug);
    }

    findTagOption = (value: string) => this.allTagsOptions.find((tag: BlogsTags) => tag.slug === value);

    scrollToTop() {
        window.document.getElementById('navbar')?.scrollIntoView();
    }

    scrollToBlogs() {
        window.document.getElementById('blogs')?.scrollIntoView();
    }

    get titleByWebsite(): string {
        return this.shortCode !== 'CX' ? 'Video Games Blog' : 'Crypto Blog';
    }

    get subCategoriesExist(): boolean {
        return this.subCategories.length > 0;
    }

    handlePagination(value: CustomEvent): void {
        return (this.page = value.detail.page);
    }

    async urlConstructor(category: string) {
        if (category && category !== 'all') {
            let cleanedCategory = category.trim();
            if (cleanedCategory === 'tips-& tricks') {
                cleanedCategory = 'tips-and-tricks';
            }
            this.router.load(`category/${cleanedCategory}`);
            return await this.pageChanged();
        }
        return this.router.load('');
    }

    async createListAuthors() {
        this.authors = [];
        const authorsFilter = await this.blogAuthorService.getByWebsiteCode();
        const newAuthors = Array.isArray(authorsFilter)
            ? authorsFilter.map((author) => {
                return {
                    label: `${author.firstName} ${author.lastName}`,
                    value: author.alias,
                    selected: false,
                };
            })
            : [];
        this.authors.push({ label: 'All', value: 'all', selected: true });
        this.authors.push(...newAuthors);
        this.authorRef.selectItem(0);

        return this.authors;
    }

    createListCategories() {
        this.categories = [];
        const categoriesFilter = this.allTagsOptions.filter(tag => tag.websites.find(web => web.website.shortCode === this.shortCode && tag.hideFromAll !== true));

        const newCategories = Array.isArray(categoriesFilter)
            ? categoriesFilter.map((category) => {
                return {
                    label: category.name,
                    value: category.name,
                    selected: this.params.category !== 'all' ? false : true,
                };
            })
            : [];

        if (!newCategories.some((category: ItemsSortBy) => category.label === 'All')) {
            newCategories.unshift({
                label: 'All',
                value: 'all',
                selected: this.params.category !== 'all' ? false : true,
            });
        }

        this.categories.push(...newCategories);
        return this.categories;
    }

    createListSubCategories() {
        this.subCategories = [];
        const selectedCategories = this.categories.filter((category) => category.selected);

        for (const category of selectedCategories) {
            const tagOption = this.allTagsOptions.find((tag) => tag.name === category.label);

            if (tagOption && tagOption.blogTagChilds && tagOption.blogTagChilds.length > 0) {
                const items: ItemsSortBy[] = tagOption.blogTagChilds.map((childTag) => ({
                    label: childTag.name,
                    value: childTag.name,
                    selected: false,
                }));

                items.unshift({
                    label: 'All',
                    value: 'all',
                    selected: true,
                });

                this.subCategories.push({
                    label: tagOption.name,
                    value: tagOption.slug,
                    items,
                });
            }
        }

        return this.subCategories;
    }

    async clearFilters() {
        this.searchQuery = '';

        if (this.authorRef) {
            this.authors.forEach((author) => (author.selected = false));
            this.authorRef.selectItem(0);
        }

        if (this.categoryRef) {
            this.categories.forEach((category) => (category.selected = true));
        }

        this.subCategories = [];

        if (this.sortRef) {
            this.items.forEach((item) => (item.selected = false));
            this.items[0].selected = true;
            this.sortRef.selectItem(0);
        }

        this.category = 'all';
        this.page = 1;

        await this.pageChanged();
    }
}
